import firestar, { Actions, CollectionDescriptor, CollectionResponse, ReadOptions, Root } from "firestar";
import CSConnect, {
   Casinos,
   Users,
   Gifts,
   Vouchers
} from './'
import Members from "./Members";
import Promotions from "./Promotions";
import Shipping from "./Shipping";
import firebase from 'firebase/compat/app'
import axios from 'axios'

export const CheckoutErrorOptions = {
   'user-not-found': 'User not found',
   'casino-not-found': 'Casino not found',
   'promotion-not-found': 'Promotion not found',
   'limit-reached': 'Claim Limit Reached',
   'promotion-expired': 'Promotion expired',
   'member-not-found': 'Member not found',
   'player-not-found': 'Player Card Number not found',
   'voucher-not-found': 'Voucher not found',
   'duplicate-claim': 'Duplicate Claim',
   'invalid-product': 'Invalid Product',
   'phone-number-mismatch': 'Phone number doesn\'t match',
   'not-at-location': 'User not at the location',
   'not-qualified': 'Not qualified for the gift.',
   'not-enabled': 'Member not enabled',
   'insufficient-balance': 'Insufficient Points Balance',
   'pin-not-found': 'Pin not found',
   'incorrect-pin': 'Incorrect Pin',
   'player-card-locked': 'Player card locked',
   'error-redeeming-points': 'Error redeeming Balance',
   'address-not-found': 'Address not found',
   'point-factor-unavailable': 'Casino does not support points',
   'other': 'Other',
   'cancelled': 'Cancelled',
}

export const OrderTypeOptions = {
   'self': 'Self',
   'bulk': 'Bulk',
   'assistant': 'Assistant'
}

export const OrderStatusOptions = {
   'created': 'Created',
   'confirmed': 'Confirmed',
   'shipped': 'Shipped',
   'completed': 'Completed',
   'failed': 'Failed',
   'cancelled': 'Cancelled'
}

export const OrderStatusColorOptions = {
   'created': 'green',
   'confirmed': 'green',
   'shipped': 'green',
   'completed': 'green',
   'failed': 'red',
   'cancelled': 'red'
}

interface CheckoutErrorType extends Error {
   code: CheckoutErrorOptionType,
   message: string
   status: 'failed' | 'success'
}

interface CheckoutResponseType extends CheckoutErrorType {
   order?: OrderType
}

export type OrderStatusType = keyof typeof OrderStatusOptions
export type OrderClaimOptionType = keyof typeof OrderTypeOptions

export type CheckoutErrorOptionType = keyof typeof CheckoutErrorOptions

export type ShopifyOrderResponse = {
   id: string,
   order_number: number,
   name: string,
   order_status_url: string,
   lineItems: {
      id: string,
      title: string,
      name: string,
      variant_id: string,
      price: string
   }[]
   [key: string]: any
}

export interface OrderType extends Root {
   metadata: ShopifyOrderResponse,
   userInfo: Users.UserInfo,
   member: Members.Member
   casinoInfo: Casinos.CasinoInfo,
   status: OrderStatusType,
   cancelReason?: string,
   product: Gifts.ShopifyProduct
   trackingLink?: string | null,
   statusHistory: OrderStatusType[]
   promotionInfo: Promotions.Promotion,
   voucherInfo?: Vouchers.VoucherInfo
   failureError?: {
      name: string,
      error: any
   },
   chats?: {
      open: number
      total: number,
      unread?: {
         support?: number,
         user?: number
      },
      updatedAt: firebase.firestore.Timestamp
   },
   lastSyncedAt?: firebase.firestore.Timestamp,
   comments?: string,
   isShippingRequired?: boolean,
   digitalProductLink?: string | null,
   type?: OrderClaimOptionType | null
   author?: Users.UserInfo | null,
   expired?: true,
   overrideTrackingLink?: string,
   playerPin?: number | null,
   dev?: {
      executionTime?: number,
      createdAt?: firebase.firestore.Timestamp,
      endedAt?: firebase.firestore.Timestamp,
   },
   pluginResponse?: {
      [key: string]: any
   },
   [key: string]: any
}

namespace Orders {

   export type Order = OrderType
   export type OrderStatus = OrderStatusType
   export type CheckoutErrorOption = CheckoutErrorOptionType
   export type CheckoutError = CheckoutErrorType
   export type OrderClaimType = OrderClaimOptionType
   export type CheckoutResponse = CheckoutResponseType

   export function createOrder(
      userInfo: Users.UserInfo,
      casinoInfo: Casinos.CasinoInfo,
      product: Gifts.ShopifyProduct,
      promotionInfo: Promotions.PromotionInfo,
      member: Members.Member,
      metadata: any,
      voucherInfo?: Vouchers.VoucherInfo,
      params?: Partial<Order>
   ) {
      return Actions.addToCollection<Order>('orders', {
         casinoInfo: casinoInfo,
         userInfo: userInfo,
         metadata: metadata,
         status: 'created',
         statusHistory: ['created'],
         product: product,
         promotionInfo: promotionInfo,
         member: member,
         voucherInfo: voucherInfo ?? null,
         ...params ?? {}
      } as Order)
   }

   export function checkout(
      userId: string,
      casinoId: string,
      promotionId: string,
      productId: string,
      memberId: string,
      voucherId?: string | null,
      validateOnly?: boolean,
      author?: Users.UserInfo,
      type?: Orders.OrderClaimType,
      pluginParams?: { [key: string]: any } | null,
      dev?: true
   ) {
      return new Promise<CheckoutResponse>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl(dev)
            const url = baseUrl + '/checkout'
            const body = {
               userId,
               casinoId,
               promotionId,
               productId,
               memberId,
               voucherId,
               validateOnly,
               author,
               type,
               pluginParams
            }

            if(dev === true) {
               console.log('Checkout Payload', JSON.stringify(body))
            }

            const response = await fetch(url, {
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               },
               method: 'POST',
               body: JSON.stringify(body)
            })
            console.log('Checkout Response', response.status, response.statusText)
            if (response.ok) {
               const responseJSON = await response.json()
               resolve(responseJSON)
            } else if (response.status === 400) {
               const responseJSON = await response.json()
               throw responseJSON;
            } else {
               throw new Error(response.statusText);
            }
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getOrders(descriptor?: CollectionDescriptor, readOptions?: ReadOptions) {
      return Actions.readCollection<Order>('orders', descriptor, readOptions)
   }

   export function getOrder(orderId: string) {
      return Actions.readDocument<Order>(`orders/${orderId}`)
   }

   export function add(orderId: string, document: Partial<Order> | Order) {
      return Actions.writeDocument<Order>(`orders/${orderId}`, document)
   }

   export function deleteOrder(orderId: string) {
      return Actions.deleteDocument(`orders/${orderId}`)
   }

   export function updateOrder(orderId: string, document: Partial<Order> | Order) {
      return Actions.updateDocument<Order>(`orders/${orderId}`, document)
   }

   export function getOrdersForPromotion(
      promotionId: string,
      descriptor?: CollectionDescriptor,
      readOptions?: ReadOptions
   ) {
      return getOrders({
         ...descriptor,
         where: [
            {
               fieldPath: 'promotionInfo.id',
               opStr: '==',
               value: promotionId
            },
            ...descriptor?.where ?? []
         ]
      },
         readOptions)
   }

   export function getOrdersForMemberWithCardNumber(
      promotionId: string,
      cardNumber: string
   ) {
      return new Promise<Order[]>(async (resolve, reject) => {
         try {
            const response = await Actions.readCollection<Order>('orders', {
               where: [
                  {
                     fieldPath: 'promotionInfo.id',
                     opStr: '==',
                     value: promotionId
                  },
                  {
                     fieldPath: 'member.cardNumber',
                     opStr: '==',
                     value: cardNumber
                  }
               ],
               limit: 10000
            })
            resolve(response.data)
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getOrdersForMemberWithPhoneNumber(
      promotionId: string,
      phoneNumber: string
   ) {
      return new Promise<Order[]>(async (resolve, reject) => {
         try {
            const response = await Actions.readCollection<Order>('orders', {
               where: [
                  {
                     fieldPath: 'promotionInfo.id',
                     opStr: '==',
                     value: promotionId
                  },
                  {
                     fieldPath: 'member.phoneNumber',
                     opStr: '==',
                     value: phoneNumber
                  }
               ],
               limit: 10000
            })
            resolve(response.data)
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getOrdersForUser(userId: string, descriptor?: CollectionDescriptor) {
      return Actions.readCollection<Order>('orders', {
         where: [
            {
               fieldPath: 'userInfo.uid',
               opStr: '==',
               value: userId
            },
            ...descriptor?.where ?? []
         ],
         limit: descriptor?.limit ?? 100,
         orderBy: descriptor?.orderBy ?? {
            fieldPath: 'createdAt',
            directionStr: 'desc'
         }
      })
   }

   export function expireOrders() {
      return new Promise<{
         total: number
      }>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()
            const url = baseUrl + '/expireOrders'
            const response = await axios.get(url, {
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })
            const responseJSON = response.data
            resolve(responseJSON)
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getShopifyOrder(metadataId: string) {
      return new Promise<{
         total: number
      }>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()
            const url = baseUrl + '/getShopifyOrder?id=' + metadataId
            const response = await axios.get(url, {
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })
            const responseJSON = response.data
            resolve(responseJSON)
         } catch (error) {
            reject(error)
         }
      })
   }

   export function updateActiveOrders(client: any, orderIds?: string[]) {
      return new Promise<Order[]>(async (resolve, reject) => {
         try {

            let ordersResponse: CollectionResponse<Order>
            if ((orderIds ?? []).length > 0) {
               ordersResponse = await Orders.getOrders({
                  where: [
                     {
                        fieldPath: 'id',
                        opStr: 'in',
                        value: orderIds
                     }
                  ]
               })
            } else {
               ordersResponse = await Orders.getOrders({
                  where: [
                     {
                        fieldPath: 'status',
                        opStr: 'in',
                        value: ['created', 'confirmed', 'shipped']
                     },
                  ],
                  orderBy: {
                     fieldPath: 'lastSyncedAt',
                     directionStr: 'asc'
                  },
                  limit: 40
               })
            }

            const orders = ordersResponse.data

            console.log('Total orders to sync:', orders.length)

            const promises = orders.map(async (order) => {
               return new Promise<Partial<Orders.Order>>(async (resolve, reject) => {
                  try {
                     const response = await client.get(`orders/${order.metadata.id}`)
                     const body = await response.json()
                     const metadata = body.order
                     resolve({
                        id: order.id,
                        metadata: metadata
                     })
                  } catch (error) {
                     console.error('Error Getting Order Metadata: ', String(error))
                     reject(error)
                  }
               })
            })

            const settledResponse = await Promise.allSettled(promises)

            const fulfilledResponses = settledResponse.filter((s) => s.status === 'fulfilled').map((s: any) => s.value) as Orders.Order[]
            const failedResponses = settledResponse.filter((s) => s.status === 'rejected')

            console.log('Fulfilled', fulfilledResponses.length)
            console.log('Failed Responses Count:', failedResponses.length)

            const firestore = firestar.getFirestore()

            await Actions.batchUpdate<Orders.Order>(fulfilledResponses.map((order) => {
               compileOrderMetadata(order)
               const provider = Shipping.getTrackingProvider(order)
               return {
                  path: `orders/${order.id}`,
                  document: {
                     metadata: order.metadata,
                     status: order.status,
                     trackingLink: provider?.trackingUrl ?? null,
                     statusHistory: order.statusHistory ?? [],
                     cancelReason: order.cancelReason ?? null,
                     lastSyncedAt: firestore.Timestamp.now()
                  } as any
               }
            }))

            resolve(fulfilledResponses)

         } catch (error) {

            reject(error)
         }
      })
   }

   export function compileOrderMetadata(order: Order) {
      const metadata = order.metadata
      let statusHistory: OrderStatus[] = ['created']
      const fulfillments = metadata.fulfillments ?? []
      if (fulfillments.length > 0) {

         statusHistory.push('confirmed')

         if ((order.isShippingRequired ?? true)) {
            const fulfillment = fulfillments[0]
            if ((fulfillment.tracking_numbers ?? []).length > 0) {
               statusHistory.push('shipped')
            }
            if ((fulfillment.trackingUrls ?? []).length > 0) {
               order.trackingLink = fulfillment.trackingUrls[0]
            }
            if (fulfillment.shipment_status === 'delivered') {
               statusHistory.push('completed')
            }
         } else {
            statusHistory.push('completed')
         }
      }
      if (metadata.cancel_reason != null) {
         statusHistory.push('cancelled')
         order.cancelReason = metadata.cancel_reason
      }
      order.status = statusHistory[statusHistory.length - 1]
      order.statusHistory = statusHistory
   }

   export function initCheckoutFail(code: CheckoutErrorOption, message?: any) {
      const error = new Error(message) as any
      error['status'] = 'failed'
      error['code'] = code
      return error
   }
}

export default Orders